import api from '../../services/api';

const normalizarNomeProduto = (valor = '') =>
  String(valor)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/**
 * Busca um produto pelo nome. Prioriza o nome exato; se não houver, pega o mais
 * curto que começe pelo termo (ex.: "Mepsevii - 5ml" quando só existe a apresentação).
 */
async function _buscarProdutoPorNome(nome) {
  const response = await api.get('produtos/', { params: { nome } });
  const data = response.data;
  const items = Array.isArray(data) ? data : (data.results ?? []);
  const alvo = normalizarNomeProduto(nome);

  const exato = items.find((item) => normalizarNomeProduto(item.nome) === alvo);
  if (exato) return exato;

  return (
    items
      .filter((item) => normalizarNomeProduto(item.nome).startsWith(alvo))
      .sort((a, b) => String(a.nome).length - String(b.nome).length)[0] || null
  );
}

/**
 * Resolve os ids de Crysvita e Mepsevii pelo nome, em vez de fixá-los no código —
 * os ids diferem entre os ambientes. Falha silenciosa: sem a permissão de consulta
 * de produtos a tela segue funcionando, apenas sem as seções de exames por produto.
 */
export async function resolverProdutosCeaf() {
  const [crysvita, mepsevii] = await Promise.all([
    _buscarProdutoPorNome('Crysvita').catch(() => null),
    _buscarProdutoPorNome('Mepsevii').catch(() => null),
  ]);

  return { crysvitaId: crysvita?.id, mepseviiId: mepsevii?.id };
}

/**
 * Envia planilha .xlsx para importação no banco via API.
 */
export async function importarPlanilhaCeaf(arquivo, programaId, produtoCrysvitaId, produtoMepseviiId) {
  const formData = new FormData();
  formData.append('arquivo', arquivo);
  formData.append('programa_id', programaId);
  if (produtoCrysvitaId) formData.append('produto_crysvita_id', produtoCrysvitaId);
  if (produtoMepseviiId) formData.append('produto_mepsevii_id', produtoMepseviiId);

  const response = await api.post('ceaf/importar/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function baixarModeloPlanilhaCeaf() {
  const response = await api.get('ceaf/importar/modelo/', {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Lista estados CEAF cadastrados no banco para um programa.
 */
export async function listarEstadosCeaf(programaId, uf) {
  const params = {};
  if (programaId) params.programa_id = programaId;
  if (uf) params.uf = uf;

  const response = await api.get('ceaf/estados/', { params });
  const data = response.data;
  return Array.isArray(data) ? data : (data.results ?? []);
}

export async function detalharEstadoCeaf(id) {
  const response = await api.get(`ceaf/estados/${id}/`);
  return response.data;
}

export async function atualizarEstadoCeaf(id, payload) {
  const response = await api.put(`ceaf/estados/${id}/`, payload);
  return response.data;
}

export async function listarDocumentosEstadoCeaf(ceafEstadoId, categoria) {
  const params = { ceaf_estado_id: ceafEstadoId };
  if (categoria) params.categoria = categoria;

  const response = await api.get('ceaf/documentos/estados/', { params });
  const data = response.data;
  return Array.isArray(data) ? data : (data.results ?? []);
}

export async function listarTiposDocumentoCeaf(categoria, nome) {
  const params = {};
  if (categoria) params.categoria = categoria;
  if (nome) params.nome = nome;

  const response = await api.get('ceaf/documentos/tipos/', { params });
  const data = response.data;
  return Array.isArray(data) ? data : (data.results ?? []);
}

export async function criarTipoDocumentoCeaf(payload) {
  const response = await api.post('ceaf/documentos/tipos/', payload);
  return response.data;
}

export async function criarDocumentoEstadoCeaf(payload) {
  const response = await api.post('ceaf/documentos/estados/', payload);
  return response.data;
}

export async function removerDocumentoEstadoCeaf(id) {
  await api.delete(`ceaf/documentos/estados/${id}/`);
}

export async function listarExamesEstadoCeaf(ceafEstadoId, produtoId) {
  const params = { ceaf_estado_id: ceafEstadoId };
  if (produtoId) params.produto_id = produtoId;

  const response = await api.get('ceaf/exames/estados/', { params });
  const data = response.data;
  return Array.isArray(data) ? data : (data.results ?? []);
}

export async function listarExamesCeaf(nome) {
  const params = {};
  if (nome) params.nome = nome;

  const response = await api.get('ceaf/exames/', { params });
  const data = response.data;
  return Array.isArray(data) ? data : (data.results ?? []);
}

export async function criarExameCeaf(payload) {
  const response = await api.post('ceaf/exames/', payload);
  return response.data;
}

export async function criarExameEstadoCeaf(payload) {
  const response = await api.post('ceaf/exames/estados/', payload);
  return response.data;
}

export async function removerExameEstadoCeaf(id) {
  await api.delete(`ceaf/exames/estados/${id}/`);
}

/**
 * Busca documentos vinculados a um ceaf_estado.
 *
 * @param {number} ceafEstadoId
 * @param {'paciente'|'medico'|undefined} categoria
 * @returns {Promise<string>} Nomes dos documentos separados por "; "
 */
async function _buscarNomesDocumentos(ceafEstadoId, categoria) {
  try {
    const params = { ceaf_estado_id: ceafEstadoId };
    if (categoria) params.categoria = categoria;
    const response = await api.get('ceaf/documentos/estados/', { params });
    const items = Array.isArray(response.data) ? response.data : (response.data.results ?? []);
    return items.map((d) => d.dados_documento?.nome || d.documento_tipo?.nome || '').filter(Boolean).join('; ');
  } catch {
    return '';
  }
}

/**
 * Busca exames vinculados a um ceaf_estado.
 *
 * @param {number} ceafEstadoId
 * @param {number|undefined} produtoId
 * @returns {Promise<string>} Nomes dos exames separados por "; "
 */
async function _buscarNomesExames(ceafEstadoId, produtoId) {
  try {
    const params = { ceaf_estado_id: ceafEstadoId };
    if (produtoId) params.produto_id = produtoId;
    const response = await api.get('ceaf/exames/estados/', { params });
    const items = Array.isArray(response.data) ? response.data : (response.data.results ?? []);
    return items.map((e) => e.dados_exame?.nome || e.exame?.nome || '').filter(Boolean).join('; ');
  } catch {
    return '';
  }
}

/**
 * Enriquece um registro de estado com docs e exames buscados da API.
 * Retorna um novo objeto com os campos `docPaciente`, `docMedico`, `examesCrysvita`, `examesMepsevii`.
 *
 * @param {object} estadoItem - Item da lista de estados (contém id, uf, etc.)
 * @param {{ crysvitaId?: number, mepseviiId?: number }} [produtoIds]
 */
export async function enriquecerEstadoComDocsExames(estadoItem, produtoIds = {}) {
  const id = estadoItem.id;
  const [docPaciente, docMedico, examesCrysvita, examesMepsevii] = await Promise.all([
    _buscarNomesDocumentos(id, 'paciente'),
    _buscarNomesDocumentos(id, 'medico'),
    produtoIds.crysvitaId ? _buscarNomesExames(id, produtoIds.crysvitaId) : Promise.resolve(''),
    produtoIds.mepseviiId ? _buscarNomesExames(id, produtoIds.mepseviiId) : Promise.resolve(''),
  ]);

  return { ...estadoItem, docPaciente, docMedico, examesCrysvita, examesMepsevii };
}
